// RewardVaultWithdrawConstructorStep.tsx
import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn, ColumnCenter } from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { AutoRow, RowBetween } from 'components/Row'
import { useCurrency } from 'hooks/Tokens'
import { usePoolUnderlyingBalances } from 'hooks/usePoolPositionDetails'
import { useMemo } from 'react'
import { Text } from 'rebass'
import { IslandApiResponse } from 'state/data/slice'
import styled from 'styled-components'

const isNegligibleAmount = (amount?: CurrencyAmount<Token>) => {
  if (!amount) return true
  return !amount.greaterThan('0')
}

const UnderlyingViewWrapper = styled(AutoRow)`
  padding-left: 0.5rem;
  flex-wrap: nowrap;
  width: auto;
`

function UnderlyingView({
  amount0,
  amount1,
  currency0,
  currency1,
}: {
  amount0: CurrencyAmount<Currency> | undefined
  amount1: CurrencyAmount<Currency> | undefined
  currency0?: Currency | null
  currency1?: Currency | null
}) {
  return (
    <UnderlyingViewWrapper gap="2px">
      <DoubleCurrencyLogo
        currency0={amount0?.currency || currency0 || undefined}
        currency1={amount1?.currency || currency1 || undefined}
        size={18}
      />
      <Text
        flexWrap="nowrap"
        fontSize={14}
        style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        overflow="hidden"
      >
        {amount0?.toSignificant(6) || '0'} {amount0?.currency.symbol || currency0?.symbol}/
        {amount1?.toSignificant(6) || '0'} {amount1?.currency.symbol || currency1?.symbol}
      </Text>
    </UnderlyingViewWrapper>
  )
}

const InfoCard = styled(LightCard)`
  margin-bottom: 16px;
`

type RewardVaultWithdrawConstructorStepProps = {
  selectedShares: CurrencyAmount<Currency> | undefined
  userBalanceShares: CurrencyAmount<Currency> | undefined
  rewardVaultStaked: CurrencyAmount<Token> | undefined
  pool: IslandApiResponse | null | undefined
  percentSelected: number // The percentage selected in the slider
  overrideCurrencyA?: Currency
  overrideCurrencyB?: Currency
  onSelect: (props: {
    poolLiquidity: CurrencyAmount<Token> | undefined
    rewardVaultAmount?: CurrencyAmount<Token>
    percentSelected: number
  }) => void
  next: () => void
}

export default function RewardVaultWithdrawConstructorStep({
  selectedShares,
  userBalanceShares,
  rewardVaultStaked,
  pool,
  percentSelected,
  overrideCurrencyA,
  overrideCurrencyB,
  onSelect,
  next,
}: RewardVaultWithdrawConstructorStepProps) {
  const token0 = useCurrency(pool?.token0.id)
  const token1 = useCurrency(pool?.token1.id)

  const baseCurrency0 = overrideCurrencyA ?? token0
  const baseCurrency1 = overrideCurrencyB ?? token1

  const displayCurrency0 = baseCurrency0 ?? undefined
  const displayCurrency1 = baseCurrency1 ?? undefined

  // Calculate underlying balances for better UI representation
  const rewardVaultUnderlyingBalances = usePoolUnderlyingBalances(
    pool || null,
    rewardVaultStaked,
    displayCurrency0,
    displayCurrency1
  )
  const freeUnderlyingBalances = usePoolUnderlyingBalances(
    pool || null,
    userBalanceShares as CurrencyAmount<Token>,
    displayCurrency0,
    displayCurrency1
  )
  const selectedUnderlyingBalances = usePoolUnderlyingBalances(
    pool || null,
    selectedShares as CurrencyAmount<Token>,
    displayCurrency0,
    displayCurrency1
  )

  const calculatedPercentage = useMemo(() => {
    if (!selectedShares || !userBalanceShares || !rewardVaultStaked) return 0

    const totalAvailable = userBalanceShares.add(rewardVaultStaked)
    if (totalAvailable.equalTo('0')) return 0

    const percentage = parseFloat(selectedShares.divide(totalAvailable).multiply('100').toFixed(2))
    return Math.min(100, Math.max(0, percentage))
  }, [selectedShares, userBalanceShares, rewardVaultStaked])

  const effectivePercentage = percentSelected > 0 ? percentSelected : calculatedPercentage

  // Calculate how much we need from each source to fulfill the requested amount
  const { poolLiquidityToWithdraw, rewardVaultAmountToWithdraw } = useMemo(() => {
    if (!selectedShares) {
      return { poolLiquidityToWithdraw: undefined, rewardVaultAmountToWithdraw: undefined }
    }

    const totalAmount = selectedShares

    // Case 1: No staked or free liquidity (should be prevented by UI, but handle gracefully)
    if (
      (!userBalanceShares || userBalanceShares.equalTo('0')) &&
      (!rewardVaultStaked || rewardVaultStaked.equalTo('0'))
    ) {
      return { poolLiquidityToWithdraw: undefined, rewardVaultAmountToWithdraw: undefined }
    }

    // Case 2: Only free liquidity available - use it all
    if (
      (!rewardVaultStaked || rewardVaultStaked.equalTo('0')) &&
      userBalanceShares &&
      userBalanceShares.greaterThan('0')
    ) {
      return {
        poolLiquidityToWithdraw: totalAmount.greaterThan(userBalanceShares)
          ? (userBalanceShares as CurrencyAmount<Token>)
          : (totalAmount as CurrencyAmount<Token>),
        rewardVaultAmountToWithdraw: undefined,
      }
    }

    // Case 3: Only reward vault liquidity available - use it all
    if (
      (!userBalanceShares || userBalanceShares.equalTo('0')) &&
      rewardVaultStaked &&
      rewardVaultStaked.greaterThan('0')
    ) {
      return {
        poolLiquidityToWithdraw: undefined,
        rewardVaultAmountToWithdraw: totalAmount.greaterThan(rewardVaultStaked)
          ? rewardVaultStaked
          : (totalAmount as CurrencyAmount<Token>),
      }
    }

    // Case 4: Both free and staked liquidity available
    if (
      userBalanceShares &&
      userBalanceShares.greaterThan('0') &&
      rewardVaultStaked &&
      rewardVaultStaked.greaterThan('0')
    ) {
      // 4.1: Free liquidity is sufficient (accounting for potential precision errors)
      if (
        totalAmount.lessThan(userBalanceShares) ||
        isNegligibleAmount(totalAmount.subtract(userBalanceShares) as CurrencyAmount<Token>)
      ) {
        return {
          poolLiquidityToWithdraw: totalAmount as CurrencyAmount<Token>,
          rewardVaultAmountToWithdraw: undefined,
        }
      }

      // 4.2: Need to use both sources
      const neededFromRewardVault = totalAmount.subtract(userBalanceShares) as CurrencyAmount<Token>

      // Ensure we're not dealing with negligible amounts due to precision errors
      if (isNegligibleAmount(neededFromRewardVault)) {
        return {
          poolLiquidityToWithdraw: totalAmount as CurrencyAmount<Token>,
          rewardVaultAmountToWithdraw: undefined,
        }
      }

      // Cap the amount from reward vault to what's available
      const actualRewardVaultAmount = neededFromRewardVault.greaterThan(rewardVaultStaked)
        ? rewardVaultStaked
        : neededFromRewardVault

      return {
        poolLiquidityToWithdraw: userBalanceShares as CurrencyAmount<Token>,
        rewardVaultAmountToWithdraw: actualRewardVaultAmount,
      }
    }

    // Default fallback - this should be unreachable given the cases above
    return { poolLiquidityToWithdraw: undefined, rewardVaultAmountToWithdraw: undefined }
  }, [selectedShares, userBalanceShares, rewardVaultStaked, percentSelected])

  // Handle confirming the withdrawal
  const onWithdraw = () => {
    onSelect({
      poolLiquidity: poolLiquidityToWithdraw,
      // Don't send negligible amounts to be unstaked
      rewardVaultAmount: isNegligibleAmount(rewardVaultAmountToWithdraw) ? undefined : rewardVaultAmountToWithdraw,
      percentSelected: effectivePercentage,
    })
    next()
  }

  // Determine if we can proceed
  const continueEnabled = useMemo(() => {
    if (!selectedShares || selectedShares.equalTo('0')) {
      return false
    }

    // Make sure we have something meaningful to withdraw
    return (
      (poolLiquidityToWithdraw && !poolLiquidityToWithdraw.equalTo('0')) ||
      (rewardVaultAmountToWithdraw &&
        !rewardVaultAmountToWithdraw.equalTo('0') &&
        !isNegligibleAmount(rewardVaultAmountToWithdraw))
    )
  }, [selectedShares, poolLiquidityToWithdraw, rewardVaultAmountToWithdraw])

  // Show account balances section
  const showBalances = useMemo(() => {
    return (
      (userBalanceShares && !userBalanceShares.equalTo('0')) || (rewardVaultStaked && !rewardVaultStaked.equalTo('0'))
    )
  }, [userBalanceShares, rewardVaultStaked])

  return (
    <ColumnCenter gap="8px">
      {/* Account balances section */}
      {showBalances && (
        <InfoCard>
          <AutoColumn gap="10px">
            <Text fontSize={16} fontWeight={500}>
              Your Balances
            </Text>

            {userBalanceShares && !userBalanceShares.equalTo('0') && (
              <AutoColumn gap="4px">
                <RowBetween>
                  <Text fontSize={14}>Free Liquidity:</Text>
                  <Text fontSize={14}>
                    {userBalanceShares.toSignificant(6)} {userBalanceShares.currency.symbol}
                  </Text>
                </RowBetween>
                <UnderlyingView
                  amount0={freeUnderlyingBalances.amount0}
                  amount1={freeUnderlyingBalances.amount1}
                  currency0={displayCurrency0}
                  currency1={displayCurrency1}
                />
              </AutoColumn>
            )}

            {rewardVaultStaked && !rewardVaultStaked.equalTo('0') && (
              <AutoColumn gap="4px">
                <RowBetween>
                  <Text fontSize={14}>Staked in Reward Vault:</Text>
                  <Text fontSize={14}>
                    {rewardVaultStaked.toSignificant(6)} {rewardVaultStaked.currency.symbol}
                  </Text>
                </RowBetween>
                <UnderlyingView
                  amount0={rewardVaultUnderlyingBalances.amount0}
                  amount1={rewardVaultUnderlyingBalances.amount1}
                  currency0={displayCurrency0}
                  currency1={displayCurrency1}
                />
              </AutoColumn>
            )}
          </AutoColumn>
        </InfoCard>
      )}

      {/* Summary of what will be withdrawn */}
      <LightCard>
        <AutoColumn gap="10px">
          <Text fontSize={16} fontWeight={500}>
            Withdrawal Summary
          </Text>

          <RowBetween>
            <Text fontSize={14}>Selected to Withdraw:</Text>
            <Text fontSize={14}>
              {selectedShares?.toSignificant(6) || '0'} {selectedShares?.currency.symbol || ''}
            </Text>
          </RowBetween>

          {poolLiquidityToWithdraw && !poolLiquidityToWithdraw.equalTo('0') && (
            <RowBetween>
              <Text fontSize={14}>From Free Liquidity:</Text>
              <Text fontSize={14}>
                {poolLiquidityToWithdraw.toSignificant(6)} {poolLiquidityToWithdraw.currency.symbol}
              </Text>
            </RowBetween>
          )}

          {rewardVaultAmountToWithdraw &&
            !rewardVaultAmountToWithdraw.equalTo('0') &&
            !isNegligibleAmount(rewardVaultAmountToWithdraw) && (
              <RowBetween>
                <Text fontSize={14}>From Reward Vault:</Text>
                <Text fontSize={14}>
                  {rewardVaultAmountToWithdraw.toSignificant(6)} {rewardVaultAmountToWithdraw.currency.symbol}
                </Text>
              </RowBetween>
            )}

          <RowBetween>
            <Text fontSize={14}>Underlying Tokens:</Text>
            <UnderlyingView
              amount0={selectedUnderlyingBalances.amount0}
              amount1={selectedUnderlyingBalances.amount1}
              currency0={displayCurrency0}
              currency1={displayCurrency1}
            />
          </RowBetween>
        </AutoColumn>
      </LightCard>

      <ButtonPrimary disabled={!continueEnabled} onClick={onWithdraw} width="100%">
        {percentSelected === 0 ? 'Select amount to withdraw' : 'Continue'}
      </ButtonPrimary>
    </ColumnCenter>
  )
}
